export const RESERVABLE_CATEGORIES = [
  { value: 'sala', label: 'Sala' },
  { value: 'istruttore', label: 'Istruttore' },
  { value: 'attrezzatura', label: 'Attrezzatura' },
];

export const TIME_SLOT_MINUTES = 30;

export const TIME_SLOT_OPTIONS = (() => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
      options.push(
        `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      );
    }
  }
  return options;
})();

export const snapTimeToStep = (time) => {
  if (!time || !/^\d{1,2}:\d{2}$/.test(time)) return time;
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m;
  const snapped = Math.round(total / TIME_SLOT_MINUTES) * TIME_SLOT_MINUTES;
  const capped = Math.min(Math.max(snapped, 0), 23 * 60 + 30);
  const hours = Math.floor(capped / 60);
  const mins = capped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

export const isTimeOnStep = (time) => {
  if (!time || !/^\d{1,2}:\d{2}$/.test(time)) return false;
  const minutes = parseInt(time.split(':')[1], 10);
  return minutes % TIME_SLOT_MINUTES === 0;
};

export const DURATION_PRESETS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 ora' },
  { value: 90, label: '1h 30m' },
  { value: 120, label: '2 ore' },
  { value: 180, label: '3 ore' },
  { value: 240, label: '4 ore' },
];

export const WEEKDAYS = [
  { dayOfWeek: 1, label: 'Lunedì' },
  { dayOfWeek: 2, label: 'Martedì' },
  { dayOfWeek: 3, label: 'Mercoledì' },
  { dayOfWeek: 4, label: 'Giovedì' },
  { dayOfWeek: 5, label: 'Venerdì' },
  { dayOfWeek: 6, label: 'Sabato' },
  { dayOfWeek: 0, label: 'Domenica' },
];

export const getCategoryLabel = (category) => {
  const match = RESERVABLE_CATEGORIES.find((c) => c.value === category);
  return match?.label || category;
};

export const getCategoryColor = (category) => {
  const colors = {
    sala: 'primary',
    istruttore: 'secondary',
    attrezzatura: 'success',
  };
  return colors[category] || 'default';
};

export const getDayName = (dayOfWeek) => {
  const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
  return days[dayOfWeek] ?? '';
};

export const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

export const createDefaultWeekSchedule = () =>
  WEEKDAYS.map(({ dayOfWeek }) => ({
    dayOfWeek,
    enabled: false,
    startTime: '09:00',
    endTime: '18:00',
  }));

export const availabilityToWeekSchedule = (availability = []) => {
  const schedule = createDefaultWeekSchedule();
  availability.forEach((slot) => {
    const day = schedule.find((d) => d.dayOfWeek === slot.dayOfWeek);
    if (day) {
      day.enabled = true;
      day.startTime = snapTimeToStep(slot.startTime);
      day.endTime = snapTimeToStep(slot.endTime);
    }
  });
  return schedule;
};

export const weekScheduleToAvailability = (weekSchedule) =>
  weekSchedule
    .filter((day) => day.enabled)
    .map(({ dayOfWeek, startTime, endTime }) => ({
      dayOfWeek,
      startTime,
      endTime,
    }));

export const getInitialReservableForm = () => ({
  name: '',
  description: '',
  category: 'sala',
  location: '',
  capacity: 1,
  maxConcurrentAccess: 1,
  costPerHour: 0,
  availableDurations: [60],
  weekSchedule: createDefaultWeekSchedule(),
  teacherId: '',
  isActive: true,
  requiresApproval: false,
  advanceBookingDays: 14,
  cancellationHours: 24,
});

export const reservableToForm = (reservable) => ({
  name: reservable.name || '',
  description: reservable.description || '',
  category: reservable.category || 'sala',
  location: reservable.location || '',
  capacity: reservable.capacity ?? 1,
  maxConcurrentAccess: reservable.maxConcurrentAccess ?? 1,
  costPerHour: reservable.costPerHour ?? 0,
  availableDurations: reservable.availableDurations?.length
    ? [...reservable.availableDurations]
    : [60],
  weekSchedule: availabilityToWeekSchedule(reservable.availability),
  teacherId: reservable.teacherId?._id || reservable.teacherId || reservable.teacher?._id || '',
  isActive: reservable.isActive !== false,
  requiresApproval: reservable.requiresApproval === true,
  advanceBookingDays: reservable.advanceBookingDays ?? 14,
  cancellationHours: reservable.cancellationHours ?? 24,
});

export const formToPayload = (form) => ({
  name: form.name.trim(),
  description: form.description?.trim() || '',
  category: form.category,
  location: form.location?.trim() || '',
  capacity: Number(form.capacity),
  maxConcurrentAccess: Number(form.maxConcurrentAccess),
  costPerHour: Number(form.costPerHour),
  availableDurations: [...form.availableDurations].sort((a, b) => a - b),
  availability: weekScheduleToAvailability(form.weekSchedule),
  teacherId: form.category === 'istruttore' ? form.teacherId : null,
  isActive: form.isActive,
  requiresApproval: form.requiresApproval,
  advanceBookingDays: Number(form.advanceBookingDays),
  cancellationHours: Number(form.cancellationHours),
});

export const formToPreviewReservable = (form, teachers = []) => {
  const teacher = teachers.find((t) => t._id === form.teacherId);
  return {
    name: form.name || 'Nome risorsa',
    description: form.description,
    category: form.category,
    location: form.location,
    capacity: form.capacity,
    maxConcurrentAccess: form.maxConcurrentAccess,
    costPerHour: form.costPerHour,
    availableDurations: form.availableDurations,
    availability: weekScheduleToAvailability(form.weekSchedule),
    requiresApproval: form.requiresApproval,
    isActive: form.isActive,
    teacher: teacher
      ? { name: teacher.name, surname: teacher.surname }
      : null,
  };
};

export const validateReservableForm = (form) => {
  const errors = {};

  if (!form.name?.trim()) errors.name = 'Il nome è obbligatorio';
  if (!form.category) errors.category = 'La categoria è obbligatoria';
  if (!form.capacity || form.capacity < 1) errors.capacity = 'Inserisci una capacità valida';
  if (!form.maxConcurrentAccess || form.maxConcurrentAccess < 1) {
    errors.maxConcurrentAccess = 'Inserisci un valore di concorrenza valido';
  }
  if (form.costPerHour === '' || form.costPerHour < 0) {
    errors.costPerHour = 'Il costo orario è obbligatorio';
  }
  if (!form.availableDurations?.length) {
    errors.availableDurations = 'Seleziona almeno una durata';
  }
  if (!weekScheduleToAvailability(form.weekSchedule).length) {
    errors.availability = 'Abilita almeno un giorno con orario di disponibilità';
  }
  if (form.category === 'istruttore' && !form.teacherId) {
    errors.teacherId = 'Seleziona un istruttore';
  }
  if (!form.advanceBookingDays || form.advanceBookingDays < 1) {
    errors.advanceBookingDays = 'Inserisci un valore valido';
  }
  if (form.cancellationHours === '' || form.cancellationHours < 0) {
    errors.cancellationHours = 'Inserisci un valore valido';
  }

  form.weekSchedule
    .filter((day) => day.enabled)
    .forEach((day) => {
      if (!isTimeOnStep(day.startTime) || !isTimeOnStep(day.endTime)) {
        errors.availability = `Gli orari devono essere a step di ${TIME_SLOT_MINUTES} minuti (es. 18:00, 18:30)`;
      }
      const start = parseInt(day.startTime.split(':')[0], 10) * 60 + parseInt(day.startTime.split(':')[1], 10);
      const end = parseInt(day.endTime.split(':')[0], 10) * 60 + parseInt(day.endTime.split(':')[1], 10);
      if (start >= end) {
        errors.availability = `Orario non valido per ${getDayName(day.dayOfWeek)}`;
      }
    });

  return errors;
};
